import styles from './index.module.css'

import get from 'lodash/get'
import intersection from 'lodash/intersection'
import * as React from 'react'

import type { WithTranslation } from '@disco/i18n/with-translation'
import { withTranslation } from '@disco/i18n/with-translation'

import type { CustomContactField } from 'types/list'
import type { DropdownOption } from 'types/ui'

import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'

import IdentifierTextField from 'features/rights-data/components/identifier-text-field'
import {
  formatIPIDisplay,
  formatISNIDisplay,
  getIdentifierValue,
  getIPIValidationError,
  getISNIValidationError,
} from 'features/rights-data/utils/identifier-validation'

import ContactCard from '../contact-card'

const proPromptTextCreator = label => `Create new PRO "${label}"`

const publisherPromptTextCreator = label => `Create new publisher "${label}"`

type Props = {
  contact: Record<string, any>
  pros: Record<string, any>[]
  publishers: Record<string, any>[]
  rightsholderPro: Record<string, any>[]
  rightsholderPublishers: Record<string, any>[]
  editMode: boolean
  onFieldChange: (...args: Array<any>) => any
  onCustomFieldChange: (...args: Array<any>) => any
  onUpdate: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
  publisherDropdownOptions: DropdownOption[]
  proDropdownOptions: DropdownOption[]
  onDefaultProSelect: (...args: Array<any>) => any
  onDefaultPublisherSelect: (...args: Array<any>) => any
  onExternalIdentifierChange: (type: 'ipi' | 'isni', value: string) => any
  onSwitchToEditMode: (...args: Array<any>) => any
  saving?: boolean
  customFields: CustomContactField[]
} & WithTranslation

type OwnState = {
  userSetDefaultPro: boolean
  userSetDefaultPublisher: boolean
  ipiError: string | null
  isniError: string | null
}

const writerTypes = ['Writer', 'Artist']

class DetailsTab extends React.Component<Props, OwnState> {
  constructor(props: Props) {
    super(props)
    this.state = {
      userSetDefaultPro: false,
      userSetDefaultPublisher: false,
      ipiError: null,
      isniError: null,
    }
  }

  getPendingIdentifierValues = () => {
    const { pendingChanges, external_identifiers } = this.props.contact
    const identifiers = get(
      pendingChanges,
      'external_identifiers',
      external_identifiers || [],
    )
    return {
      ipi: getIdentifierValue(identifiers, 'ipi') || '',
      isni: getIdentifierValue(identifiers, 'isni') || '',
    }
  }

  update = (...args) => {
    const { pendingChanges, tags } = this.props.contact
    const tagsValue = get(pendingChanges, 'tags', tags || [])
    const showIdentifierFields = tagsValue.includes('Writer')
    const { ipi, isni } = this.getPendingIdentifierValues()
    const { t } = this.props
    const ipiError = showIdentifierFields ? getIPIValidationError(ipi, t) : null
    const isniError = showIdentifierFields
      ? getISNIValidationError(isni, t)
      : null
    this.setState({ ipiError, isniError })
    if (ipiError || isniError) {
      return
    }
    this.props.onUpdate(...args)
  }

  externalIdentifierChange = (type: 'ipi' | 'isni', value: string) => {
    if (type === 'ipi') {
      this.setState({ ipiError: null })
    } else {
      this.setState({ isniError: null })
    }
    this.props.onExternalIdentifierChange(type, value)
  }

  defaultProValueSet = (...args) => {
    this.setState({ userSetDefaultPro: true })
    this.props.onDefaultProSelect(...args)
  }

  defaultPublisherValueSet = (...args) => {
    this.setState({ userSetDefaultPublisher: true })
    this.props.onDefaultPublisherSelect(...args)
  }

  cancel = (...args) => {
    this.setState({
      userSetDefaultPublisher: false,
      userSetDefaultPro: false,
      ipiError: null,
      isniError: null,
    })
    this.props.onCancel(...args)
  }

  render(): React.ReactNode {
    const {
      contact: {
        name,
        email,
        company,
        phone,
        territory,
        tags,
        acts,
        disco_user,
        referenced_business,
        pendingChanges,
        default_pro,
        default_publisher,
        custom_data,
        entity_details,
        user_specified_details,
      },
      publishers: publisherList,
      pros: proList,
      rightsholderPro,
      rightsholderPublishers,
      proDropdownOptions,
      publisherDropdownOptions,
      editMode,
      saving,
      onFieldChange,
      onCustomFieldChange,
      onCancel,
      onDefaultPublisherSelect,
      onDefaultProSelect,
      onSwitchToEditMode,
      customFields,
    } = this.props
    const nameValue = get(pendingChanges, 'name', name || '')
    const emailValue = get(pendingChanges, 'email', email || '')
    const companyValue = get(pendingChanges, 'company', company || '')
    const phoneValue = get(pendingChanges, 'phone', phone || '')
    const territoryValue = get(pendingChanges, 'territory', territory || '')
    const tagsValue = get(pendingChanges, 'tags', tags || [])

    const pendingEntityDetails = get(
      pendingChanges,
      'entity_details',
      entity_details || {},
    )
    const entityDefaultPro = pendingEntityDetails.default_pro_id
    const entityDefaultPublisher = pendingEntityDetails.default_publisher_id

    let contactDefaultPro = null
    let contactDefaultPublisher = null

    // If the user has changed the state of the dropdown we don't want
    // to override with legacy data.
    if (!this.state.userSetDefaultPro && !(user_specified_details === true)) {
      contactDefaultPro = default_pro
    }

    if (
      !this.state.userSetDefaultPublisher &&
      !(user_specified_details === true)
    ) {
      contactDefaultPublisher = default_publisher
    }

    if (contactDefaultPro) {
      const targetPro = proList.find(p => p.id === contactDefaultPro)
      const matchedRightsholderPro = targetPro
        ? rightsholderPro.find(rhp => rhp.name === targetPro.name)
        : null
      contactDefaultPro = matchedRightsholderPro
        ? matchedRightsholderPro.id
        : null
    }

    if (contactDefaultPublisher) {
      const targetPublisher = publisherList.find(
        p => p.id === contactDefaultPublisher,
      )
      const matchedRightsholderPublisher = targetPublisher
        ? rightsholderPublishers.find(rhp => rhp.name === targetPublisher.name)
        : null
      contactDefaultPublisher = matchedRightsholderPublisher
        ? matchedRightsholderPublisher.id
        : null
    }

    // Distinguish explicit `null` (staged clear — either from the user
    // clearing the dropdown, or from tag removal wiping the default) from
    // `undefined` (never set), so a clear doesn't fall back to legacy data.
    const finalDefaultProId =
      entityDefaultPro !== undefined ? entityDefaultPro : contactDefaultPro
    const finalDefaultPublisherId =
      entityDefaultPublisher !== undefined
        ? entityDefaultPublisher
        : contactDefaultPublisher

    const defaultPro = finalDefaultProId
      ? rightsholderPro.find(p => p.id === finalDefaultProId)
      : null

    const defaultPublisher = finalDefaultPublisherId
      ? rightsholderPublishers.find(p => p.id === finalDefaultPublisherId)
      : null

    const actsValue = get(pendingChanges, 'acts', acts || '')
    const showExtraFields = intersection(writerTypes, tagsValue).length > 0

    const { ipi: ipiValue, isni: isniValue } = this.getPendingIdentifierValues()
    const { ipiError, isniError } = this.state
    const showIdentifierFields = tagsValue.includes('Writer')

    return editMode ? (
      <ContactCard>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Name"
              name="name"
              value={nameValue}
              onChange={onFieldChange}
              required
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Email"
              name="email"
              value={emailValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Company"
              name="company"
              value={companyValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Phone"
              name="phone"
              value={phoneValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Location"
              name="territory"
              value={territoryValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        {showExtraFields && (
          <React.Fragment>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label caption="Default Publisher" />
                <CustomDropdown
                  name="publisher"
                  placeholder="Select default publisher"
                  promptTextCreator={publisherPromptTextCreator}
                  onChange={this.defaultPublisherValueSet}
                  value={finalDefaultPublisherId}
                  options={publisherDropdownOptions}
                  clearable
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label="Acts"
                  name="acts"
                  value={actsValue}
                  onChange={onFieldChange}
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom>
              <div>
                <Label caption="Default PRO" />
                <CustomDropdown
                  name="pro"
                  placeholder="Select PRO..."
                  promptTextCreator={proPromptTextCreator}
                  onChange={this.defaultProValueSet}
                  value={finalDefaultProId}
                  options={proDropdownOptions}
                  clearable
                />
              </div>
            </Row>
          </React.Fragment>
        )}
        {showIdentifierFields && (
          <React.Fragment>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <IdentifierTextField
                  identifierType="ipi"
                  label="IPI"
                  name="ipi"
                  value={ipiValue}
                  error={ipiError}
                  onChange={value =>
                    this.externalIdentifierChange('ipi', value)
                  }
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom>
              <div>
                <IdentifierTextField
                  identifierType="isni"
                  label="ISNI"
                  name="isni"
                  value={isniValue}
                  error={isniError}
                  onChange={value =>
                    this.externalIdentifierChange('isni', value)
                  }
                />
              </div>
            </Row>
          </React.Fragment>
        )}
        {customFields.map(field => {
          const fieldValue = get(
            pendingChanges,
            `custom_data.${field.name}`,
            custom_data[field.name] || '',
          )
          return (
            <Row key={field.name} columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label={field.name}
                  name={field.name}
                  value={fieldValue}
                  onChange={onCustomFieldChange}
                />
              </div>
            </Row>
          )
        })}
        <ButtonContainer>
          <Button
            appearance="primary"
            caption="Update contact details"
            disabled={!!referenced_business}
            onClick={this.update}
            progressIndicator={saving}
            progressCaption="Updating contact"
          />
          <Button caption="Cancel" onClick={this.cancel} />
        </ButtonContainer>
      </ContactCard>
    ) : (
      <ContactCard
        action={{
          content: 'Edit',
          disabled: !!referenced_business,
          onClick: onSwitchToEditMode,
        }}
      >
        <div>
          <strong>{name}</strong>
        </div>
        <div>{email}</div>
        <table className={styles.detailsTab}>
          <tbody>
            <tr>
              <td className={styles.detailsTabFieldCaption}>Company</td>
              <td>{company}</td>
            </tr>
            {disco_user &&
              disco_user.businesses &&
              disco_user.businesses.length > 0 && (
                <tr>
                  <td className={styles.detailsTabFieldCaption}>DISCOs</td>
                  <td>{disco_user.businesses.map(b => b.name).join(', ')}</td>
                </tr>
              )}
            <tr>
              <td className={styles.detailsTabFieldCaption}>Phone</td>
              <td>{phone}</td>
            </tr>
            <tr>
              <td className={styles.detailsTabFieldCaption}>Location</td>
              <td>{territory}</td>
            </tr>
            {showExtraFields && (
              <React.Fragment>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>
                    Default Publisher
                  </td>
                  <td>{defaultPublisher && defaultPublisher.name}</td>
                </tr>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>Acts</td>
                  <td>{acts}</td>
                </tr>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>Default PRO</td>
                  <td>{defaultPro && defaultPro.name}</td>
                </tr>
              </React.Fragment>
            )}
            {showIdentifierFields && (
              <React.Fragment>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>IPI</td>
                  <td>{formatIPIDisplay(ipiValue)}</td>
                </tr>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>ISNI</td>
                  <td>{formatISNIDisplay(isniValue)}</td>
                </tr>
              </React.Fragment>
            )}
            {customFields.map(field => (
              <tr key={field.name}>
                <td className={styles.detailsTabFieldCaption}>{field.name}</td>
                <td>{custom_data[field.name]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ContactCard>
    )
  }
}

const TranslatedDetailsTab: React.ComponentType<
  Omit<Props, keyof WithTranslation>
> = withTranslation('rights')(DetailsTab)

export default TranslatedDetailsTab
